import { ViewportScroller } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { GoogleMap, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { Config } from '@app/@core/config';
import { CardList } from '@app/@shared/models/card-list';
import { CardModel } from '@shared/models/card.model';
import { CardService } from '../card.service';
import { encode } from '@googlemaps/polyline-codec';

declare const require: any;

@Component({
  selector: 'app-card-print',
  templateUrl: './card-print.component.html',
  styleUrls: ['./card-print.component.css'],
})
export class CardPrintComponent implements OnInit, AfterViewInit {
  @ViewChild('map_result') map: GoogleMap;
  @ViewChild(MapInfoWindow) infoWindow: MapInfoWindow;
  @HostListener('window:load', ['$event'])
  public card: CardModel;
  public cards: CardList[];
  public id: string;
  public zoom = 14;
  public icon: google.maps.Icon;
  public center: google.maps.LatLng;
  public options: google.maps.MapOptions = {
    mapTypeControl: false,
    fullscreenControl: false,
    zoomControl: false,
    streetViewControlOptions: {
      position: google.maps.ControlPosition.TOP_CENTER,
    },
    gestureHandling: 'greedy',
    styles: [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }],
      },
    ],
  };
  public clusterImage: string;
  public clusterOptions: MarkerClustererOptions;
  public mapType: google.maps.MapTypeId;
  public circuitTypes: Array<boolean>;
  private fakeLoadingTime: number = 600; // It is important to note that these fake loading times are here to
  private fakeLoadingTimeMobile: number = 600; // give time to let the google API process the map request
  public kmlData: any;
  public kmlUrl:string;
  public baseUrl:string = window.location.origin;
  public staticMapsUrl: string;

  constructor(
    private cardService: CardService,
    private viewport: ViewportScroller,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.syncData();
  }
  ngAfterViewInit(): void {
    this.cdr.detectChanges();
    this.timeout();
  }

  timeout() {
    setTimeout(() => {
      console.log(this.map);
      if (this.map === undefined) this.timeout();
      else window.print();
    }, 600);
  }
  onLoad(event: Event): void {
    // Code à exécuter après le chargement complet de la page
    console.log('La page a été complètement chargée.');
  }

  private async syncData(): Promise<void> {
    const isMobile =
      /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    if (!isMobile) {
      window.onafterprint = function () {
        history.back();
      };
    }

    const url = window.location.href;
    const id = url.substring(url.indexOf('print/') + 6);
    this.card = await this.cardService.getCard(id).toPromise();
    this.kmlUrl = `${this.baseUrl}${this.card.kmlPath}`;
    this.kmlData = this.card.kmlPath
      ? await this.cardService.getCardKml(this.card.kmlPath).toPromise()
      : null;
    this.circuitTypes = Config.circuit.map(
      (c) => (c.id & this.card.circuitType) > 0
    );

    this.staticMapsUrl = this.constructStaticMapsUrl();
  }

  public doesImageExists(index: number): boolean {
    return index in this.card.images;
  }

  public getImageUrl(index: number): string {
    if (!(index in this.card.images)) return 'assets/images/placeholder.jpg';
    return this.card.images[index].url;
  }

  public getCollectivities(): string {
    try {
      let collectivitiesStringify: string = '';
      this.card.group.collectivities.forEach((elem) => {
        collectivitiesStringify = collectivitiesStringify + ` ${elem.name}`;
      });
      return collectivitiesStringify;
    } catch (error) {
      return '';
    }
  }

  public getName(): string {
    try {
      return this.card.city.name;
    } catch (error) {
      return '';
    }
  }

  public getCategory(): string {
    if (this.card.category == 0) return 'Patrimoine';
    return 'Circuit';
  }

  public getSubCategory(): string {
    if (this.card.category == 0) return this.card.subcategory.name;
    if (!this.circuitTypes) this.circuitTypes = [];

    let subcategStringyfied: string = ``;
    let i: number = 0;
    if (this.circuitTypes[0] && i < 2) {
      subcategStringyfied = `${subcategStringyfied}À pied / `;
      i++;
    }
    if (this.circuitTypes[1] && i < 2) {
      subcategStringyfied = `${subcategStringyfied}À vélo - VTT / `;
      i++;
    }
    if (this.circuitTypes[2] && i < 2) {
      subcategStringyfied = `${subcategStringyfied}À vélo - VTC / `;
      i++;
    }
    if (this.circuitTypes[3] && i < 2) {
      subcategStringyfied = `${subcategStringyfied}À vélo - sur route / `;
      i++;
    }
    if (this.circuitTypes[4] && i < 2) {
      subcategStringyfied = `${subcategStringyfied}À cheval / `;
      i++;
    }
    if (this.circuitTypes[5] && i < 2) {
      subcategStringyfied = `${subcategStringyfied}Trail / `;
      i++;
    }
    if (subcategStringyfied.length < 3) return '';
    if (i == 2) return `${subcategStringyfied.slice(0, -3)}...`;
    return subcategStringyfied.slice(0, -3);
  }

  public constructStaticMapsUrl(): string {
    // Base url for the API
    const baseUrl =
      'https://maps.googleapis.com/maps/api/staticmap?key=AIzaSyCdbZt5TbqZhx45jrE6wAbVkAJgbWFjJZk'; // Because we love when things like that are hardcoded in

    // Start/origin point of the current card
    const originCoords = this.card.position;

    // If there is a path to display, get the path
    const encodedPathFromKml = this.kmlData ? this.getPathFromKml() : null;

    // Base map style and size of the generated picture
    const mapType = 'roadmap';
    const mapSize = '600x400';

    // Disable all default Google poi
    const mapStyle = 'feature:poi|visibility:off';

    // Displays a red, slightly translucid path
    const pathStyle = 'weight:3|color:0xcc1d14b3';

    let finalUrl = `${baseUrl}&maptype=${mapType}&style=${mapStyle}&size=${mapSize}&markers=${originCoords.lat},${originCoords.lng}&path=${pathStyle}`;
    if (encodedPathFromKml !== null) {
      finalUrl = finalUrl + `|enc:${encodedPathFromKml}`;
    }
    return encodeURI(finalUrl);
  }

  private getPathFromKml() {
    // Extracts coords from kmlData and returns an encoded version to fit in the url

    // Get the str version of coords
    try {
      const convert = require('xml-js');
      const options = { compact: true };
      const jsonKml = JSON.parse(convert.xml2json(this.kmlData, options));
      const coordsListTxt =
        jsonKml.kml?.Document?.Placemark?.LineString?.coordinates?._text ?? '';

      // Generate the list of lat;lng coordinates
      const coordsList = this.generateCoordsList(coordsListTxt);
      const encodedCoordsList = encode(coordsList, 5);
      return encodedCoordsList;
    } catch (error) {}
  }

  private generateCoordsList(coordsListTxt: string) {
    const rawList = coordsListTxt.split(' ');
    return rawList.map((elem) => {
      const currentCoord = elem.split(',');
      // 0 = lng, 1 = lat
      return [parseFloat(currentCoord[1]), parseFloat(currentCoord[0])];
    });
  }

  onMapReady(map: google.maps.Map): void {
    console.log('La carte est prête !', map);
  }
}
